/**
 * The brand colours' light twins (Jay, 2026-09-29: "show the spectrum in both versions").
 *
 * Each Golden Solar spectrum colour has a twin for thin graphics on light pages — lines, dots, edges,
 * chart marks. The twin keeps the original's exact OKLCH hue and chroma, and only its lightness is
 * lowered until it clears 3:1 on the light canvas (#FEFEFE). A colour that already clears 3:1 is its
 * own twin. Where sRGB cannot hold the original's chroma at that lightness, the twin takes the most
 * chroma sRGB allows (`srgbLimited`). Large fills, illustrations and the logo keep the originals.
 *
 * This table is the one place the pairs live: the brand guide's §03 is rendered from it
 * (`docs/brand/colour-twins-render.mjs`), `colour-twins.guard.test.ts` holds every pair to the rule
 * and the guide to the table, and the website's `phase-tones.guard.test.ts` holds its `--phase-*`
 * tokens to `JOURNEY_FAMILIES`.
 *
 * Strings and pure functions only (`@featit/shared` is platform-agnostic).
 */

/** The light theme's page canvas, the ground a twin must clear 3:1 on. */
export const LIGHT_CANVAS = '#FEFEFE';
/** The dark theme's page canvas. */
export const DARK_CANVAS = '#212121';

export interface IColourTwin {
  original: string;
  twin: string;
  /** sRGB cannot hold the original's chroma at the twin's lightness; the twin takes the most it can. */
  srgbLimited: boolean;
}

export interface IBrandSpectrum {
  id: string;
  name: string;
  stops: readonly IColourTwin[];
}

/**
 * The three spectra, in the order the icon draws them from the centre outward. Honey, lime and teal's
 * twins are the tones Jay picked for the journey (2026-09-29); they sit within 0.007 OKLab (ΔE) of
 * the lightness-only search's first 8-bit hit, which is below what an eye can tell apart.
 */
export const BRAND_SPECTRUMS: readonly IBrandSpectrum[] = [
  {
    id: 'solar-core',
    name: 'Solar core',
    stops: [
      { original: '#FFE397', twin: '#AC9147', srgbLimited: false },
      { original: '#FFBD53', twin: '#C58702', srgbLimited: true },
      { original: '#F28A2D', twin: '#E07910', srgbLimited: false },
    ],
  },
  {
    id: 'bioluminous-ring',
    name: 'Bioluminous ring',
    stops: [
      { original: '#C8DF69', twin: '#899D1C', srgbLimited: false },
      { original: '#99CF5C', twin: '#6FA22C', srgbLimited: false },
      { original: '#5DB779', twin: '#4BA569', srgbLimited: false },
      { original: '#31A99E', twin: '#2AA49A', srgbLimited: false },
    ],
  },
  {
    id: 'liquid-frame',
    name: 'Liquid frame',
    stops: [
      { original: '#31A99E', twin: '#2AA49A', srgbLimited: false },
      { original: '#23B5C7', twin: '#02A1B2', srgbLimited: true },
      { original: '#288EE0', twin: '#288EE0', srgbLimited: false },
      { original: '#3D67DE', twin: '#3D67DE', srgbLimited: false },
    ],
  },
];

export interface IJourneyFamily {
  /** The website's `--phase-<phase>` token. */
  phase: 'idea' | 'shape' | 'make' | 'deliver';
  family: string;
  /** The `--tier-<tier>` alias on /pricing. */
  tier: string;
  dark: string;
  light: string;
}

/**
 * The four journey families (brand guide §03). Honey, lime and teal are spectrum stops, so their
 * light tone is that stop's twin. Azure's dark tone, #5B82EC, is a lighter azure than the frame's
 * #3D67DE, drawn for the dark canvas; it already clears 3:1 on the light canvas, so it is its own
 * twin and the journey uses one azure in both themes.
 */
export const JOURNEY_FAMILIES: readonly IJourneyFamily[] = [
  { phase: 'idea', family: 'Honey', tier: 'free', dark: '#FFBD53', light: '#C58702' },
  { phase: 'shape', family: 'Lime', tier: 'team', dark: '#99CF5C', light: '#6FA22C' },
  { phase: 'make', family: 'Teal', tier: 'business', dark: '#23B5C7', light: '#02A1B2' },
  { phase: 'deliver', family: 'Azure', tier: 'enterprise', dark: '#5B82EC', light: '#5B82EC' },
];

/**
 * The ground the iOS tab bar draws its selected item's label on: a grey pill, measured in the
 * iOS 26.5 simulator (I-0977). It is the hardest ground the highlight meets as text.
 */
export const SELECTED_TAB_GROUND = { light: '#EBEBEB', dark: '#3D3D3D' } as const;

/**
 * The phone's highlight: the colour the iOS shell tints its native chrome with — the tab bar's active
 * item, navigation buttons (I-0977, Jay 2026-09-30: "the orange only highlight color on phone does
 * not look good and does not fit the color space well"). Each is a Liquid Frame stop toned by
 * `toneOnCanvas` to 4.5:1 on its theme's `SELECTED_TAB_GROUND`, where a tab label sits: the
 * frame's azure #3D67DE in light, the frame's blue #288EE0 in dark. `AccentColor.colorset` is held
 * to this pair by `highlight.guard.test.ts` (concept-brand-identity.md §8.2, HL-1…HL-4).
 */
export const HIGHLIGHT = { light: '#3962D8', dark: '#4FADFF' } as const;

function channels(hex: string): number[] {
  return [1, 3, 5].map((index) => parseInt(hex.slice(index, index + 2), 16) / 255);
}

function linear(c: number): number {
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

/** WCAG 2.2 contrast ratio between two `#RRGGBB` colours. */
export function contrastRatio(a: string, b: string): number {
  const luminance = (hex: string) => {
    const [r, g, bl] = channels(hex).map((c) =>
      c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4,
    );
    return 0.2126 * r + 0.7152 * g + 0.0722 * bl;
  };
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

/** `#RRGGBB` → OKLCH (Björn Ottosson's OKLab): lightness 0–1, chroma, hue in degrees. */
export function hexToOklch(hex: string): { l: number; c: number; h: number } {
  const [r, g, b] = channels(hex).map(linear);
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  const labA = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
  const labB = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;
  return {
    l: 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    c: Math.hypot(labA, labB),
    h: ((Math.atan2(labB, labA) * 180) / Math.PI + 360) % 360,
  };
}

function inSrgb(l: number, c: number, h: number): boolean {
  const labA = c * Math.cos((h * Math.PI) / 180);
  const labB = c * Math.sin((h * Math.PI) / 180);
  const lc = (l + 0.3963377774 * labA + 0.2158037573 * labB) ** 3;
  const mc = (l - 0.1055613458 * labA - 0.0638541728 * labB) ** 3;
  const sc = (l - 0.0894841775 * labA - 1.291485548 * labB) ** 3;
  const rgb = [
    4.0767416621 * lc - 3.3077115913 * mc + 0.2309699292 * sc,
    -1.2684380046 * lc + 2.6097574011 * mc - 0.3413193965 * sc,
    -0.0041960863 * lc - 0.7034186147 * mc + 1.707614701 * sc,
  ];
  return rgb.every((v) => v >= -1e-6 && v <= 1 + 1e-6);
}

/** The most OKLCH chroma sRGB can show at this lightness and hue. */
export function maxSrgbChroma(l: number, h: number): number {
  let low = 0;
  let high = 0.4;
  for (let step = 0; step < 40; step++) {
    const mid = (low + high) / 2;
    if (inSrgb(l, mid, h)) low = mid;
    else high = mid;
  }
  return low;
}

/** OKLCH → `#RRGGBB`, each channel clamped into sRGB and rounded to 8 bits. */
export function oklchToHex(l: number, c: number, h: number): string {
  const labA = c * Math.cos((h * Math.PI) / 180);
  const labB = c * Math.sin((h * Math.PI) / 180);
  const lc = (l + 0.3963377774 * labA + 0.2158037573 * labB) ** 3;
  const mc = (l - 0.1055613458 * labA - 0.0638541728 * labB) ** 3;
  const sc = (l - 0.0894841775 * labA - 1.291485548 * labB) ** 3;
  const gamma = (v: number) => (v <= 0.0031308 ? 12.92 * v : 1.055 * v ** (1 / 2.4) - 0.055);
  return `#${[
    4.0767416621 * lc - 3.3077115913 * mc + 0.2309699292 * sc,
    -1.2684380046 * lc + 2.6097574011 * mc - 0.3413193965 * sc,
    -0.0041960863 * lc - 0.7034186147 * mc + 1.707614701 * sc,
  ]
    .map((v) => Math.round(Math.min(1, Math.max(0, gamma(Math.min(1, Math.max(0, v))))) * 255))
    .map((v) => v.toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase()}`;
}

/**
 * The twin rule as a function, for any colour on any canvas: the tone of `hex` that clears
 * `minimum`:1 on `canvas`. It keeps the colour's OKLCH hue and chroma and moves only its lightness,
 * away from the canvas (darker on a light one, lighter on a dark one), by the least that clears the
 * ratio; where sRGB cannot hold the chroma at that lightness it takes the most sRGB allows. A colour
 * that already clears is returned as given.
 *
 * The table above is this rule applied to the brand colours on `LIGHT_CANVAS`, picked by hand to the
 * nearest 8-bit tone. A colour a person chooses, such as a work item type's (CT-6e, I-0913), is
 * derived here rather than stored twice.
 */
export function toneOnCanvas(hex: string, canvas: string, minimum = 3): string {
  if (contrastRatio(hex, canvas) >= minimum) return hex;
  const { l, c, h } = hexToOklch(hex);
  const at = (lightness: number) =>
    oklchToHex(lightness, Math.min(c, maxSrgbChroma(lightness, h)), h);
  let near = l;
  let far = hexToOklch(canvas).l < 0.5 ? 1 : 0;
  for (let step = 0; step < 32; step++) {
    const mid = (near + far) / 2;
    if (contrastRatio(at(mid), canvas) >= minimum) far = mid;
    else near = mid;
  }
  return at(far);
}

/** Signed hue difference in degrees, −180 to 180. */
export function hueDifference(from: number, to: number): number {
  return ((to - from + 540) % 360) - 180;
}
